import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { usersAPI } from '../services/api';

const Users = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'staff',
  });
  const [editUser, setEditUser] = useState({
    id: null,
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'staff',
    is_active: true,
  });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.list();
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setCreating(true);
      setError('');
      const response = await usersAPI.create(newUser);
      if (response.data.success) {
        setDialogOpen(false);
        setNewUser({
          username: '',
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'staff',
        });
        loadUsers();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (userRow) => {
    setEditUser({
      id: userRow.id,
      username: userRow.username || '',
      email: userRow.email || '',
      password: '',
      first_name: userRow.first_name || '',
      last_name: userRow.last_name || '',
      role: userRow.role || 'staff',
      is_active: userRow.is_active !== undefined ? userRow.is_active : true,
    });
    setEditDialogOpen(true);
    setError('');
  };

  const handleUpdateUser = async () => {
    try {
      setUpdating(true);
      setError('');
      const response = await usersAPI.update(editUser);
      if (response.data.success) {
        setEditDialogOpen(false);
        setEditUser({
          id: null,
          username: '',
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'staff',
          is_active: true,
        });
        loadUsers();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren des Benutzers');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClick = (userRow) => {
    setSelectedUser(userRow);
    setDeleteDialogOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      setError('');
      const response = await usersAPI.delete(selectedUser.id);
      if (response.data.success) {
        setDeleteDialogOpen(false);
        setSelectedUser(null);
        loadUsers();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Löschen des Benutzers');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'username',
      headerName: 'Benutzername',
      width: 150,
    },
    {
      field: 'email',
      headerName: 'E-Mail',
      width: 200,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 180,
      valueGetter: (params) => `${params.row.first_name || ''} ${params.row.last_name || ''}`.trim() || '-',
    },
    {
      field: 'role',
      headerName: 'Rolle',
      width: 120,
      renderCell: (params) => {
        const colors = {
          admin: 'error',
          manager: 'warning',
          staff: 'info',
        };
        return <Chip label={params.value} color={colors[params.value]} size="small" />;
      },
    },
    {
      field: 'is_active',
      headerName: 'Aktiv',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Ja' : 'Nein'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'last_login',
      headerName: 'Letzter Login',
      width: 180,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString('de-DE') : 'Nie',
    },
    {
      field: 'created_at',
      headerName: 'Erstellt',
      width: 180,
      valueFormatter: (params) => new Date(params.value).toLocaleString('de-DE'),
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        user?.role === 'admin' ? (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              color="info"
              onClick={() => handleEditClick(params.row)}
              size="small"
              title="Bearbeiten"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="error"
              onClick={() => handleDeleteClick(params.row)}
              size="small"
              title="Löschen"
              disabled={params.row.id === user?.id}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null
      ),
    },
  ];

  // Only admin can access this page
  if (user?.role !== 'admin' && user?.role !== 'manager') {
    return (
      <Box>
        <Alert severity="error">Sie haben keine Berechtigung, diese Seite anzuzeigen.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          Benutzer
        </Typography>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Neuer Benutzer
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={users}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              loading={loading}
              disableSelectionOnClick
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Benutzer erstellen</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Benutzername"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Passwort"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vorname"
                value={newUser.first_name}
                onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nachname"
                value={newUser.last_name}
                onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Rolle"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={creating || !newUser.username || !newUser.email || !newUser.password}
          >
            {creating ? <CircularProgress size={24} /> : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Benutzer bearbeiten</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Benutzername"
                value={editUser.username}
                onChange={(e) => setEditUser({ ...editUser, username: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Passwort (leer lassen, um nicht zu ändern)"
                type="password"
                value={editUser.password}
                onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                helperText="Nur ausfüllen, wenn Sie das Passwort ändern möchten"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vorname"
                value={editUser.first_name}
                onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nachname"
                value={editUser.last_name}
                onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Rolle"
                value={editUser.role}
                onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
              >
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={editUser.is_active ? 1 : 0}
                onChange={(e) => setEditUser({ ...editUser, is_active: e.target.value === 1 })}
              >
                <MenuItem value={1}>Aktiv</MenuItem>
                <MenuItem value={0}>Inaktiv</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={updating}>
            Abbrechen
          </Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            disabled={updating || !editUser.username || !editUser.email}
          >
            {updating ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Benutzer löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den Benutzer <strong>{selectedUser?.username}</strong> wirklich löschen?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Diese Aktion kann nicht rückgängig gemacht werden!
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
